import { Select } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { FaRegClock } from "react-icons/fa";
import timeOptions from "../../constants/timeOptions";
import CustomDatePicker from "./CustomDatePicker";

const CustomDateTimePicker = ({
    label,
    value,
    onChange,
    placeholder,
    timePlaceholder,
    disabled = false,
    insideModal = false,
    error,
    textColor = "gray",
    maxDate,
    minDate,
    disablePast = true,
    showNowButton = false,
    className = "",
    filterPastTimeFromDate,
}) => {
    const [selectedTime, setSelectedTime] = useState("");
    const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
    const [timeOptionsFiltered, setTimeOptionsFiltered] = useState(timeOptions);
    const isInternalChangeRef = useRef(false);

    useEffect(() => {
        if (isInternalChangeRef.current) {
            isInternalChangeRef.current = false;
            return;
        }

        if (value) {
            const hasTimePart = value.includes(" ") && value.trim().split(" ").length >= 2;
            if (hasTimePart) {
                setSelectedTime(dayjs(value).format("HH:mm"));
            } else {
                setSelectedTime("");
            }
        } else {
            setSelectedTime("");
        }
    }, [value]);

    useEffect(() => {
        if (!value) {
            setTimeOptionsFiltered(timeOptions);
            return;
        }

        const datePart = value.includes(" ") ? value.split(" ")[0] : value;
        const selectedDate = dayjs(datePart).format("MM/DD/YYYY");
        const filterDate = filterPastTimeFromDate
            ? dayjs(filterPastTimeFromDate).format("MM/DD/YYYY")
            : dayjs().format("MM/DD/YYYY");

        let filtered = timeOptions;

        if (selectedDate === filterDate && disablePast) {
            const currentTime = filterPastTimeFromDate
                ? dayjs(filterPastTimeFromDate).format("HH:mm")
                : dayjs().format("HH:mm");
            const timeNow = dayjs(currentTime, "HH:mm");
            filtered = filtered.filter((option) => dayjs(option.value, "HH:mm").isAfter(timeNow));
        }

        setTimeOptionsFiltered(filtered);
    }, [value, disablePast, filterPastTimeFromDate]);

    const handleDateChange = (dateString) => {
        const formattedDateString = dateString ? dayjs(dateString).format("MM/DD/YYYY") : "";
        setSelectedTime("");
        setTimeDropdownOpen(true);
        onChange(formattedDateString);
    };

    const handleTimeChange = (timeString) => {
        isInternalChangeRef.current = true;
        setSelectedTime(timeString);
        const datePart = value ? (value.includes(" ") ? value.split(" ")[0] : value) : "";
        if (datePart) {
            onChange(`${dayjs(datePart).format("MM/DD/YYYY")} ${timeString}`);
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {label && (
                <div className="mb-2">
                    <span className="text-sm font-semibold" style={{ color: textColor }}>
                        {label}
                    </span>
                </div>
            )}

            <div className="grid w-full grid-cols-12 gap-0">
                <div className="col-span-8">
                    <CustomDatePicker
                        showNowButton={showNowButton}
                        style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            borderRightWidth: "1px",
                            flex: 2,
                        }}
                        insideModal={insideModal}
                        disabled={disabled}
                        placeholder={placeholder}
                        showTime={false}
                        value={value ? new Date(value) : undefined}
                        onChange={handleDateChange}
                        disablePast={disablePast}
                        maxDate={maxDate}
                        minDate={minDate}
                        error={error}
                    />
                </div>

                <div className="col-span-4">
                    <Select
                        status={(error && "error") || ""}
                        open={timeDropdownOpen}
                        onOpenChange={setTimeDropdownOpen}
                        suffixIcon={<FaRegClock style={{ color: error ? "red" : "" }} />}
                        className="insurance-sale-time-select"
                        style={{
                            pointerEvents: disabled ? "none" : undefined,
                            width: "100%",
                            borderRadius: "6px",
                            color: disabled ? "#AAAAAA" : "gray",
                            borderTopLeftRadius: "0px",
                            borderBottomLeftRadius: "0px",
                        }}
                        placeholder={timePlaceholder || "Time"}
                        value={selectedTime || undefined}
                        onChange={handleTimeChange}
                        options={timeOptionsFiltered}
                        disabled={disabled}
                        getPopupContainer={(triggerNode) =>
                            triggerNode.parentElement?.parentElement?.parentElement || document.body
                        }
                    />
                </div>
            </div>

            {error && (
                <div className="mt-1">
                    <span className="text-xs text-red-500">{error}</span>
                </div>
            )}
        </div>
    );
};

CustomDateTimePicker.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    timePlaceholder: PropTypes.string,
    disabled: PropTypes.bool,
    insideModal: PropTypes.bool,
    error: PropTypes.string,
    textColor: PropTypes.string,
    maxDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    disablePast: PropTypes.bool,
    showNowButton: PropTypes.bool,
    className: PropTypes.string,
    filterPastTimeFromDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

CustomDateTimePicker.defaultProps = {
    label: undefined,
    value: undefined,
    placeholder: undefined,
    timePlaceholder: undefined,
    disabled: false,
    insideModal: false,
    error: undefined,
    textColor: "gray",
    maxDate: undefined,
    minDate: undefined,
    disablePast: true,
    showNowButton: false,
    className: "",
    filterPastTimeFromDate: undefined,
};

export default CustomDateTimePicker;
